/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { createDialogRoute } from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { DialogInput } from '@gh/core-ui';
import { fromPairs, isEmpty, pickBy } from 'lodash';

export type SetFilterDialogConfig = {
  title: string;
  groupTitle: string;
  items: AutocompleteItem[];
  selected: ID[];
};

@Component({
  selector: 'gh-set-filter-dialog',
  template: require('./set-filter-dialog.component.html'),
  styles: [require('./set-filter-dialog.component.scss')],
})
export class SetFilterDialogComponent implements DialogInput<SetFilterDialogConfig>, OnInit {
  input: SetFilterDialogConfig;

  title: string;
  groupTitle: string;
  items: AutocompleteItem[];

  checked: Hash<boolean>;
  hasSelected: boolean;
  hasAllSelected: boolean;
  hasSomeSelected: boolean;

  blankText = this.translations.get('lbl_blank_in_brackets');

  constructor(private dialogRef: MatDialogRef<any>,
              private translations: TranslationService) {
  }

  ngOnInit(): void {
    this.title = this.input.title;
    this.groupTitle = this.input.groupTitle;
    this.items = this.input.items;

    const { selected } = this.input;
    this.checked = fromPairs(isEmpty(selected)
      ? this.items.map(({ id }) => [id, true])
      : selected.map((id) => [id, true]));
    this.updateSelectedFlags();
  }

  onItemChange(id: ID, checked: boolean): void {
    this.checked[id] = checked;
    this.updateSelectedFlags();
  }

  onShowAllChange(checked: boolean): void {
    this.checked = checked ? fromPairs(this.items.map(({ id }) => [id, true])) : {};
    this.updateSelectedFlags();
  }

  onSelect(): void {
    this.dialogRef.close(this.hasAllSelected ? [] : this.getSelectedIds());
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  isBlank(item: AutocompleteItem): boolean {
    return !Boolean(item.id);
  }

  private updateSelectedFlags(): void {
    const selected = this.getSelectedIds();
    this.hasSelected = selected.length > 0;
    this.hasAllSelected = selected.length === this.items.length;
    this.hasSomeSelected = this.hasSelected && !this.hasAllSelected;
  }

  private getSelectedIds(): ID[] {
    return Object.keys(pickBy(this.checked, Boolean));
  }
}

export const setFilterDialogRoute = createDialogRoute({
  component: SetFilterDialogComponent,
});
